import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { clearToken } from '../utils/tokenStorage';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, LogOut, Star, User, MessageCircle, Inbox, XCircle } from 'lucide-react';
import ChatWindow from '../components/ChatWindow';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'bookings');

  useEffect(() => {
    // Check for tab query param
    const tab = searchParams.get('tab');
    if (tab === 'messages') {
      setActiveTab('messages');
    }
  }, [searchParams]);

  useEffect(() => {
    // Redirect owners/overseers/admins to admin panel
    if (user?.role === 'admin' || user?.role === 'owner' || user?.role === 'overseer') {
      navigate('/admin');
      return;
    }
    // Redirect artists to artist dashboard
    if (user?.role === 'artist') {
      navigate('/artist-dashboard');
      return;
    }
    fetchBookings();
  }, [user]);

  const fetchBookings = async () => {
    try {
      const response = await apiClient.get('/bookings');
      const list = Array.isArray(response.data) ? response.data : [];
      setBookings(list);
      
      // Fetch conversations for all active bookings (roster mode — no payment gating)
      const paidBookings = list.filter(b => b.booking_status !== 'cancelled');
      const conversationsData = await Promise.all(
        paidBookings.map(async (booking) => {
          try {
            const messages = await apiClient.get(`/messages/booking/${booking.booking_id}`);
            const artist = await apiClient.get(`/artists/${booking.artist_id}`);
            return {
              booking,
              artist: artist.data,
              messages: messages.data,
              unreadCount: messages.data.filter(m => m.receiver_id === user.user_id && !m.read).length
            };
          } catch (err) {
            console.error(`Error fetching conversation for booking ${booking.booking_id}:`, err);
            return null;
          }
        })
      );
      setConversations(conversationsData.filter(c => c !== null));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
      clearToken();
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBecomeArtist = () => {
    navigate('/artist/create');
  };

  const handleCancelBooking = async (bookingId) => {
    const reason = window.prompt('Please provide a reason for cancellation:');
    if (reason === null) return; // User clicked cancel
    
    try {
      await apiClient.post(`/bookings/${bookingId}/cancel?reason=${encodeURIComponent(reason)}`);
      toast.success('Cancellation request submitted. We will review it shortly.');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request cancellation');
    }
  };

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="glass-default rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img src={user.picture} alt="Profile" className="w-16 h-16 rounded-full border-2 border-violet-500" />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold truncate">Welcome, {user?.name}!</h1>
              <p className="text-white/60">Role: {user?.role}</p>
            </div>
          </div>
          {user?.role === 'user' && (
            <div className="mt-4">
              <Button
                data-testid="become-artist-button"
                onClick={handleBecomeArtist}
                className="w-full sm:w-auto bg-gradient-to-r from-fuchsia-600 to-red-600 hover:from-fuchsia-500 hover:to-red-500"
              >
                <Star className="mr-2" size={18} />
                Become an Artist
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="glass-default border border-white/10 p-1">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-violet-600">
              <Calendar size={18} className="mr-2" />
              My Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-violet-600 relative">
              <MessageCircle size={18} className="mr-2" />
              Messages ({conversations.length})
              {conversations.filter(c => c.unreadCount > 0).length > 0 && (
                <span className="ml-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {conversations.filter(c => c.unreadCount > 0).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : bookings.length === 0 ? (
            <Card className="glass-default border-white/10">
              <CardContent className="p-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-white/40" />
                <p className="text-xl text-white/60 mb-4">No bookings yet</p>
                <Button
                  onClick={() => navigate('/browse')}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
                >
                  Browse Artists
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookings.map((booking) => (
                <Card
                  key={booking.booking_id}
                  data-testid={`booking-card-${booking.booking_id}`}
                  className="glass-default border-white/10 hover-lift"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        booking.booking_status === 'confirmed'
                          ? 'bg-green-500/20 text-green-300'
                          : booking.booking_status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : booking.booking_status === 'rejected'
                          ? 'bg-red-500/20 text-red-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {booking.booking_status}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-white/60">
                        <Calendar size={16} />
                        <span>{new Date(booking.event_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/60">
                        <User size={16} />
                        <span>Artist ID: {booking.artist_id.substring(0, 12)}...</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/10 space-y-3">
                      <Button
                        data-testid={`chat-booking-${booking.booking_id}`}
                        onClick={() => setSelectedBooking(booking)}
                        className="w-full bg-violet-600 hover:bg-violet-500"
                        size="sm"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        Message Artist
                      </Button>
                      {booking.booking_status !== 'cancelled' && booking.booking_status !== 'cancellation_requested' && booking.booking_status !== 'completed' && (
                        <Button
                          variant="outline"
                          onClick={() => handleCancelBooking(booking.booking_id)}
                          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/20"
                          size="sm"
                        >
                          <XCircle size={16} className="mr-2" />
                          Request Cancellation
                        </Button>
                      )}
                      {booking.booking_status === 'cancellation_requested' && (
                        <p className="text-center text-sm text-yellow-400">Cancellation pending review</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="space-y-6">
              {conversations.length === 0 ? (
                <Card className="glass-default border-white/10">
                  <CardContent className="p-12 text-center">
                    <Inbox size={48} className="mx-auto mb-4 text-white/40" />
                    <p className="text-xl text-white/60 mb-4">No conversations yet</p>
                    <p className="text-white/40">Complete a booking to start messaging with artists</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {conversations.map((conversation) => (
                    <Card
                      key={conversation.booking.booking_id}
                      data-testid={`conversation-card-${conversation.booking.booking_id}`}
                      className="glass-default border-white/10 hover-lift cursor-pointer"
                      onClick={() => setSelectedBooking(conversation.booking)}
                    >
                      <CardContent className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {conversation.artist?.media_urls?.[0] ? (
                              <img 
                                src={conversation.artist.media_urls[0]} 
                                alt="Artist"
                                className="w-10 h-10 rounded-full border-2 border-violet-500 object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full border-2 border-violet-500 bg-violet-500/20 flex items-center justify-center">
                                <User size={20} className="text-violet-400" />
                              </div>
                            )}
                            <div>
                              <h3 className="font-semibold">Artist Booking</h3>
                              <p className="text-sm text-white/60">{conversation.artist?.genres?.[0] || 'Artist'}</p>
                            </div>
                          </div>
                          {conversation.unreadCount > 0 && (
                            <span className="w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-white/60">
                            <Calendar size={16} />
                            <span>{new Date(conversation.booking.event_date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2 text-white/60">
                            <MessageCircle size={16} />
                            <span>{conversation.messages.length} messages</span>
                          </div>
                        </div>

                        {conversation.messages.length > 0 && (
                          <div className="pt-4 border-t border-white/10">
                            <p className="text-sm text-white/60 truncate">
                              Last: {conversation.messages[conversation.messages.length - 1].content}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Chat Window */}
        {selectedBooking && (
          <ChatWindow
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
          />
        )}
      </div>
    </div>
  );
}
